/**
 * useGoalEngine.ts
 *
 * Auto-generates and tracks goals/achievements from platform actions.
 * Goals are persisted to StorageService under 'goals' and 'achievements'.
 *
 * Usage:
 *   const goalEngine = useGoalEngine();
 *   goalEngine.onPathAdded('broad_stem_tech', 'STEM & Technology');
 *   goalEngine.onGamePlayed();
 *   goalEngine.checkAchievements(student);
 */

import { StorageService } from './services/storageService';
import { AuthStore } from './stores/AuthStore';
import { Goal, Achievement } from '@/types';
import { sampleAchievements } from '@/data/sample/achievements';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function genId(): string {
  return `goal_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function getCurrentStudent() {
  const user = AuthStore.getState().currentUser;
  if (!user) return null;
  const students = StorageService.read('students');
  return students?.find((s: any) => s.userId === user._id || s.email === user.email) ?? null;
}

function getCurrentUserId(): string | null {
  return AuthStore.getState().currentUser?._id ?? null;
}

function loadGoals(userId: string): Goal[] {
  const all: Goal[] = StorageService.read('goals');
  if (!Array.isArray(all)) return [];
  return all.filter((g) => g.userId === userId);
}

function saveGoal(goal: Goal): void {
  const all: Goal[] = StorageService.read('goals');
  const idx = all.findIndex((g) => g.id === goal.id);
  if (idx >= 0) {
    all[idx] = goal;
  } else {
    all.push(goal);
  }
  StorageService.write('goals', all);
}

function loadAchievements(userId: string): Achievement[] {
  const all: Achievement[] = StorageService.read('achievements');
  if (!Array.isArray(all) || all.length === 0) return sampleAchievements;
  return all.filter((a) => a.userId === userId);
}

function saveAchievement(ach: Achievement): void {
  const all: Achievement[] = StorageService.read('achievements');
  const idx = all.findIndex((a) => a.id === ach.id);
  if (idx >= 0) {
    all[idx] = ach;
  } else {
    all.push(ach);
  }
  StorageService.write('achievements', all);
}

/** Recompute progress 0–100 from currentValue / targetValue */
function computeProgress(current: number, target: number): number {
  if (target <= 0) return 100;
  return Math.min(100, Math.round((current / target) * 100));
}

// ─── Achievement condition evaluator ────────────────────────────────────────

function evaluateAchievements(student: any): void {
  const userId = student.userId ?? student._id;
  const achs = loadAchievements(userId);
  const traitProfile: Record<string, number> = student.traitProfile ?? {};
  const maxTraitScore = Math.max(0, ...Object.values(traitProfile));
  const gameEvents = (student.traitEvents ?? []).filter((e: any) => e.source === 'game');
  const courseEvents = (student.traitEvents ?? []).filter((e: any) => e.source === 'course');
  const activityLog: any[] = student.activityLog ?? [];
  const paths: string[] = student.selectedBroadPaths ?? [];

  const conditions: Record<string, boolean> = {
    ach_first_path: paths.length >= 1,
    ach_first_module: courseEvents.length >= 1,
    ach_five_games: gameEvents.length >= 5,
    ach_trait_70: maxTraitScore >= 70,
    ach_trait_80: maxTraitScore >= 80,
    ach_assessment_done: student.traitProfile != null,
    ach_three_activities: activityLog.length >= 3,
    ach_ten_activities: activityLog.length >= 10,
  };

  let anyChanged = false;
  for (const ach of achs) {
    if (ach.unlocked) continue;
    const met = conditions[ach.id];
    if (met) {
      ach.unlocked = true;
      ach.unlockedAt = new Date();
      ach.progress = 100;
      ach.requirements = { ...ach.requirements, currentProgress: ach.requirements.threshold };
      saveAchievement(ach);
      anyChanged = true;
    } else {
      // Update partial progress
      let current = 0;
      switch (ach.id) {
        case 'ach_five_games': current = gameEvents.length; break;
        case 'ach_trait_70': current = maxTraitScore; break;
        case 'ach_trait_80': current = maxTraitScore; break;
        case 'ach_three_activities': current = activityLog.length; break;
        case 'ach_ten_activities': current = activityLog.length; break;
        case 'ach_streak_7': current = student.streakDays ?? 0; break;
        case 'ach_level_5': current = student.level ?? 1; break;
      }
      const newProgress = computeProgress(current, ach.requirements.threshold);
      if (newProgress !== ach.progress) {
        ach.progress = newProgress;
        ach.requirements = { ...ach.requirements, currentProgress: current };
        saveAchievement(ach);
      }
    }
  }
}

// ─── Goal engine public API ──────────────────────────────────────────────────

export const goalEngine = {
  /**
   * Seed initial goals from existing student state.
   * Call once when the student logs in.
   */
  initGoals(student: any): void {
    const userId = student.userId ?? student._id;
    const existing = StorageService.read('goals') as Goal[];

    // If goals already seeded for this user, skip
    if (Array.isArray(existing) && existing.some((g) => g.userId === userId)) return;

    // Seed assessment goal as completed if trait profile already exists
    if (student.traitProfile != null) {
      const id = genId();
      saveGoal({
        id, _id: id, userId,
        title: 'Complete the Psychometric Assessment',
        description: 'Take the psychometric test to map your traits and unlock career suggestions.',
        category: 'career',
        goalType: 'complete_assessment',
        tier: 'level',
        autoGenerated: true,
        targetValue: 1, currentValue: 1, progress: 100, status: 'completed',
        priority: 'high', difficulty: 'easy', xpReward: 50,
        deadline: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        createdAt: new Date(), updatedAt: new Date(),
      } as Goal);
    }

    // Seed path goals for already-selected paths
    const paths: string[] = student.selectedBroadPaths ?? [];
    for (const pathId of paths) {
      const pathName = pathId
        .replace('broad_', '')
        .replace('_', ' & ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
      this.onPathAdded(pathId, pathName, userId);
    }

    evaluateAchievements(student);
  },

  /** Call when a student adds a new broad path */
  onPathAdded(broadPathId: string, broadName: string, overrideUserId?: string): void {
    const userId = overrideUserId ?? getCurrentUserId();
    if (!userId) return;

    const existing = loadGoals(userId);
    const alreadyHas = (type: string) =>
      existing.some((g) => g.goalType === type && g.linkedPathId === broadPathId);

    const now = new Date();
    const deadline30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const deadline7 = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    const templates: Partial<Goal>[] = [
      !alreadyHas('path_start') && {
        goalType: 'path_start', tier: 'level', targetValue: 1, currentValue: 1,
        progress: 100, status: 'completed',
        title: `Begin your ${broadName} journey`,
        description: `You've added ${broadName} to your career paths. Explore courses and resources to kick things off.`,
        priority: 'high', difficulty: 'easy', xpReward: 30, deadline: deadline30,
        icon: '🚀', color: '#0ea5e9',
      },
      !alreadyHas('complete_modules') && {
        goalType: 'complete_modules', tier: 'weekly', targetValue: 5, currentValue: 0,
        progress: 0, status: 'not-started',
        title: `Complete 5 modules on your ${broadName} path`,
        description: `Work through 5 lessons in your ${broadName} career path.`,
        priority: 'high', difficulty: 'medium', xpReward: 100, deadline: deadline30,
        icon: '📚', color: '#6366f1',
      },
      !alreadyHas('watch_videos') && {
        goalType: 'watch_videos', tier: 'weekly', targetValue: 5, currentValue: 0,
        progress: 0, status: 'not-started',
        title: `Watch 5 career videos on your ${broadName} path`,
        description: `Watch 5 career exploration videos related to ${broadName}.`,
        priority: 'medium', difficulty: 'easy', xpReward: 50, deadline: deadline30,
        icon: '🎬', color: '#8b5cf6',
      },
      !alreadyHas('path_quiz') && {
        goalType: 'path_quiz', tier: 'level', targetValue: 1, currentValue: 0,
        progress: 0, status: 'not-started',
        title: `Take the ${broadName} personality match quiz`,
        description: `Complete the personality quiz for ${broadName} to see how well it matches your traits.`,
        priority: 'medium', difficulty: 'easy', xpReward: 60, deadline: deadline30,
        icon: '🎯', color: '#f59e0b',
      },
      !alreadyHas('play_games') && {
        goalType: 'play_games', tier: 'daily', targetValue: 3, currentValue: 0,
        progress: 0, status: 'not-started',
        title: `Play 3 games related to your ${broadName} path`,
        description: `Play 3 subject games to earn trait points for your ${broadName} path.`,
        priority: 'medium', difficulty: 'easy', xpReward: 40, deadline: deadline7,
        icon: '🎮', color: '#10b981',
      },
    ].filter(Boolean) as Partial<Goal>[];

    for (const tpl of templates) {
      const id = genId();
      const goal: Goal = {
        id, _id: id, userId,
        category: 'career',
        linkedPathId: broadPathId,
        autoGenerated: true,
        createdAt: now, updatedAt: now,
        ...tpl,
      } as Goal;
      saveGoal(goal);
    }

    // Re-check achievements
    const student = getCurrentStudent();
    if (student) evaluateAchievements(student);
  },

  /** Call when a student completes a learning module */
  onModuleComplete(pathId: string): void {
    const userId = getCurrentUserId();
    if (!userId) return;

    const goals = loadGoals(userId);
    const target = goals.find(
      (g) => g.goalType === 'complete_modules' && g.linkedPathId === pathId
    );
    if (!target || target.status === 'completed') return;

    target.currentValue = Math.min(target.targetValue, (target.currentValue ?? 0) + 1);
    target.progress = computeProgress(target.currentValue, target.targetValue);
    target.status = target.progress >= 100 ? 'completed' : 'in-progress';
    target.updatedAt = new Date();
    saveGoal(target);

    const student = getCurrentStudent();
    if (student) evaluateAchievements(student);
  },

  /** Call whenever a game is played */
  onGamePlayed(): void {
    const userId = getCurrentUserId();
    if (!userId) return;

    const goals = loadGoals(userId);
    const gameGoals = goals.filter((g) => g.goalType === 'play_games' && g.status !== 'completed');
    for (const g of gameGoals) {
      g.currentValue = Math.min(g.targetValue, (g.currentValue ?? 0) + 1);
      g.progress = computeProgress(g.currentValue, g.targetValue);
      g.status = g.progress >= 100 ? 'completed' : 'in-progress';
      g.updatedAt = new Date();
      saveGoal(g);
    }

    const student = getCurrentStudent();
    if (student) evaluateAchievements(student);
  },

  /** Call whenever an activity is logged */
  onActivityLogged(): void {
    const userId = getCurrentUserId();
    if (!userId) return;

    const goals = loadGoals(userId);
    const logGoals = goals.filter((g) => g.goalType === 'log_activity' && g.status !== 'completed');
    for (const g of logGoals) {
      g.currentValue = Math.min(g.targetValue, (g.currentValue ?? 0) + 1);
      g.progress = computeProgress(g.currentValue, g.targetValue);
      g.status = g.progress >= 100 ? 'completed' : 'in-progress';
      g.updatedAt = new Date();
      saveGoal(g);
    }

    const student = getCurrentStudent();
    if (student) evaluateAchievements(student);
  },

  /** Call after any trait mutation with the new score */
  onTraitUpdated(traitId: string, newScore: number): void {
    const userId = getCurrentUserId();
    if (!userId) return;

    const goals = loadGoals(userId);
    const traitGoals = goals.filter(
      (g) => g.goalType === 'improve_trait' && g.linkedTraitId === traitId && g.status !== 'completed'
    );
    for (const g of traitGoals) {
      g.currentValue = newScore;
      g.progress = computeProgress(newScore, g.targetValue);
      g.status = g.progress >= 100 ? 'completed' : 'in-progress';
      g.updatedAt = new Date();
      saveGoal(g);
    }
  },

  /** Re-evaluate all achievement conditions for a given student */
  checkAchievements(student: any): void {
    evaluateAchievements(student);
  },
};
